import { createContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import { BACK_BUTTON_ENABLED_INTERVIEW_TYPES } from './constants';
import BulletList from './components/BulletList';
import CauseSelection from '../CauseSelection';
import DurationInput from '../DurationInput';
import Options from '../Options';
import { Paragraph } from '../Layout/Embedded';
import ProfileInput from '../ProfileInput';
import IdentifiersForm from '../IdentifiersForm';
import InsuranceForm from '../InsuranceForm';
import ContactForm from '../ContactForm';
import { QuestionType } from 'src/packages/polaris/types/interviews';
import TextBoxInput from '../TextBoxInput';
import useInterviewType from '../../hooks/useInterviewType';
import { QuestionMedia } from '../Media';
import MedicationQuestion from '../MedicationQuestion';

import type { QuestionProps } from './types';

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  .MuiInputBase-input {
    font-size: 16px;
  }

  textarea::placeholder {
    ${({ theme }) => theme.typography.variants.primary.main};
    color: ${({ theme }) => theme.palette.gray[70]};
    opacity: 1;
  }
`;

const QuestionContext = createContext<Partial<QuestionProps>>({});

const Question = ({
  commonCauses,
  introText,
  onQuestionSubmit,
  question,
  totalQuestions,
  shouldShowBackButton,
  shouldShowSubmitButton,
}: QuestionProps) => {
  const interviewType = useInterviewType();
  const [parsedQuestionText, setParsedQuestionText] = useState(
    question?.text?.replace(/\[|\]/g, '') ?? null,
  );

  const bulletList = (question?.displayList ?? []).length > 0 ? question?.displayList : null;
  const hasBackButton =
    BACK_BUTTON_ENABLED_INTERVIEW_TYPES.includes(interviewType) || shouldShowBackButton;
  const subText = question?.subText;
  const context = useMemo(
    () => ({
      onQuestionSubmit,
      question,
      shouldShowBackButton: hasBackButton,
      shouldShowSubmitButton:
        question?.index === 0 ||
        question?.choice === QuestionType.MultipleChoice ||
        question?.choice === QuestionType.Duration ||
        question?.choice === QuestionType.Profile ||
        question?.choice === QuestionType.TextBox ||
        question?.choice === QuestionType.Medication ||
        question?.choice === QuestionType.FreeTextSymptom ||
        shouldShowSubmitButton,
      totalQuestions,
    }),
    [hasBackButton, onQuestionSubmit, question, totalQuestions],
  );

  const InputComponent = useMemo(() => {
    if (!question || !context.question) {
      return null;
    }

    switch (question.choice) {
      case QuestionType.Cause:
        return (
          <CauseSelection
            {...context}
            commonCauses={commonCauses}
            onQuestionSubmit={onQuestionSubmit}
            question={question}
          />
        );
      case QuestionType.Duration:
        return <DurationInput {...context} />;
      case QuestionType.Profile:
        return <ProfileInput {...context} />;
      case QuestionType.FreeTextSymptom:
      case QuestionType.TextBox:
        return <TextBoxInput {...context} />;
      case QuestionType.Identifiers:
        return <IdentifiersForm question={question} onQuestionSubmit={onQuestionSubmit} />;
      case QuestionType.Insurance:
        return <InsuranceForm question={question} onQuestionSubmit={onQuestionSubmit} />;
      case QuestionType.Contact:
        return <ContactForm question={question} onQuestionSubmit={onQuestionSubmit} />;
      case QuestionType.Medication:
        return (
          <MedicationQuestion
            question={question}
            onQuestionSubmit={onQuestionSubmit}
            setParsedQuestionText={setParsedQuestionText}
          />
        );
      default:
        return (
          <Options
            key={question.index}
            isMultiSelect={question.choice === QuestionType.MultipleChoice}
            showSubmitButton={question.index === 0 || context.shouldShowSubmitButton}
            {...context}
            question={question}
          />
        );
    }
  }, [commonCauses, context, question]);

  return (
    <>
      <Grid item className="bp-shrink-0">
        {introText && (
          <Grid>
            <Paragraph className="bp-pb-4">{introText}</Paragraph>
          </Grid>
        )}
        {parsedQuestionText && (
          <Typography style={{ whiteSpace: 'pre-line' }}>{parsedQuestionText}</Typography>
        )}
        {question && <QuestionMedia media={question.media} mediaAltText={question.media_alttext} />}
        {bulletList && <BulletList items={bulletList} />}
        {subText && <Paragraph className="bp-pt-5">{subText}</Paragraph>}
      </Grid>
      <Grid item className="bp-mr-[-8px] bp-flex bp-flex-grow bp-flex-col bp-pr-2">
        <QuestionContext.Provider value={context}>
          <StyledForm>{InputComponent}</StyledForm>
        </QuestionContext.Provider>
      </Grid>
    </>
  );
};

export { QuestionContext };
export default Question;
